import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setActive((index + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [active]);

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(active + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[680px]">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`年轻的母亲1 ${slide.title}`}
            className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-site flex-col justify-end px-4 pb-20 md:px-6 md:pb-24">
        <div className="max-w-2xl" key={active}>
          <span className="mb-4 inline-flex animate-fade-up items-center gap-2 rounded-full bg-accent-500/95 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" />
            {heroSlides[active].tag}
          </span>
          <h1 className="animate-fade-up text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {heroSlides[active].title}
          </h1>
          <p className="mt-4 max-w-xl animate-fade-up text-sm leading-relaxed text-background-100/90 md:text-base">
            {heroSlides[active].subtitle}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={heroSlides[active].link}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/category/episodes"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览全部剧集
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`切换到 ${slide.title}`}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? "w-8 bg-accent-500" : "w-3 bg-background-100/50 hover:bg-background-100"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => goTo(active - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(active + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div
        className="absolute inset-0 z-[5]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        aria-hidden="true"
      />
    </section>
  );
}